import { CallStatusEnum } from "@/lib/enum/call-status.enum";
import { Filter, FilterConfig, FilterTypes, IsFilterable } from "@/lib/filterable";
import callService from "@/services/shared/callService";
import {
  MrfiktivRealtimeSessionViewModelGen,
  MrfiktivTranscriptItemGen,
  MrfiktivToolCallGen,
  MrfiktivCallTrackingViewModelGen,
  MrfiktivRealtimeSessionControllerGetUsageParamsGen,
  MrfiktivReferenceGen
} from "@/services/mrfiktiv/v1/data-contracts";
import { CallDataAccessLayer } from "@/store/modules/access-layers/call.access-layer";
import { ITimestamp, Timestamp } from "./timestamp.entity";
import { ByAt, IByAt, ClosedByAt, IClosedByAt } from "./by-at.entity";
import { IHasViewed } from "@/lib/interfaces/has-viewed.interface";
import { PartnerUserModule } from "@/store/modules/partner-user.store";
import { BackendResourceEnum } from "@/store/enum/authResourceEnum";
import { $t } from "@/lib/utility/t";
import { IEntity } from "@/lib/utility/data/entity.interface";

@IsFilterable
class CallBase
  implements IEntity<ICall, MrfiktivCallTrackingViewModelGen>, MrfiktivRealtimeSessionViewModelGen, IHasViewed
{
  @FilterConfig({
    type: FilterTypes.OBJECT_ID,
    displayName: "objects.call.id"
  })
  id: string;

  partnerId: string;

  @FilterConfig({
    type: FilterTypes.STRING,
    displayName: "objects.call.from"
  })
  from: string;

  @FilterConfig({
    type: FilterTypes.STRING,
    displayName: "objects.call.to"
  })
  to: string;

  @FilterConfig({
    type: FilterTypes.ENUM,
    displayName: "objects.call.status",
    config: {
      items: Object.values(CallStatusEnum).map((v) => ({
        text: `enums.CallStatusEnum.${v}`,
        value: v
      })),
      itemValue: "value"
    }
  })
  status: CallStatusEnum;

  startedAt: string;

  get startedAtDate() {
    return new Date(this.startedAt);
  }

  endedAt?: string;

  get endedAtDate() {
    if (this.endedAt) {
      return new Date(this.endedAt);
    }
  }

  duration?: number;

  summary?: string;

  personId?: string;

  callSid?: string;

  realtimeSessionId?: string;

  agentConfigId?: string;

  transcriptItems?: MrfiktivTranscriptItemGen[];

  toolCalls?: MrfiktivToolCallGen[];

  callTracking?: MrfiktivCallTrackingViewModelGen[];

  refs?: MrfiktivReferenceGen[];

  metadata?: object;

  @FilterConfig({
    displayName: "objects.call.assignees",
    type: FilterTypes.OBJECT_ID,
    width: "120",
    config: {
      itemCallback: () => PartnerUserModule.paginationList,
      mapItemToComponent: (item) => ({ item }),
      itemValue: "id",
      component: "refs-user",
      refType: BackendResourceEnum.USER
    }
  })
  assignees: string[] = [];

  @FilterConfig({ type: ByAt, displayName: "objects.call.viewed" })
  viewed: IByAt[] = [];

  @FilterConfig({
    type: FilterTypes.BOOLEAN,
    displayName: "objects.call.isRead"
  })
  isRead = false;

  @FilterConfig({ type: ClosedByAt, displayName: "objects.call.closed" })
  closed?: IClosedByAt;

  get isClosed(): boolean {
    return !!this.closed?.at;
  }

  @FilterConfig({
    type: Timestamp
  })
  timestamp: ITimestamp;

  loading = false;

  get displayTitle(): string {
    if (this.summary) {
      return this.summary.length > 140 ? this.preview + "..." : this.preview;
    }

    return this.from || this.id;
  }

  get preview() {
    if (!this.summary) {
      return "";
    }

    const body = this.summary.replace("<br>", " ");
    return body.replace(/<[^>]+>/g, "").slice(0, 140);
  }

  get statusColor(): string {
    switch (this.status) {
      case CallStatusEnum.ACTIVE:
        return "info";
      case CallStatusEnum.COMPLETED:
        return "success";
      case CallStatusEnum.FAILED:
        return "error";
      case CallStatusEnum.TERMINATED:
        return "warning";
      default:
        return "grey";
    }
  }

  /**
   * Gets the readable status text
   */
  get statusReadable() {
    return $t(`enums.CallStatusEnum.${this.status}`);
  }

  get durationFormatted(): string {
    if (this.duration == null) return "";
    const minutes = Math.floor(this.duration / 60);
    const seconds = this.duration % 60;
    return `${minutes}:${String(seconds).padStart(2, "0")}`;
  }

  constructor(call?: Partial<MrfiktivRealtimeSessionViewModelGen>) {
    this.id = call?.id ?? "";
    this.partnerId = call?.partnerId ?? "";
    this.from = call?.from ?? "";
    this.to = call?.to ?? "";
    this.status = (call?.status as CallStatusEnum) ?? CallStatusEnum.ACTIVE;
    this.startedAt = call?.startedAt ?? "";
    this.endedAt = call?.endedAt;
    this.duration = call?.duration;
    this.summary = call?.summary;
    this.personId = call?.personId;
    this.callSid = call?.callSid;
    this.realtimeSessionId = call?.realtimeSessionId;
    this.agentConfigId = call?.agentConfigId;
    this.transcriptItems = call?.transcriptItems;
    this.toolCalls = call?.toolCalls;
    this.callTracking = call?.callTracking;
    this.refs = call?.refs;
    this.metadata = call?.metadata;
    this.assignees = call?.assignees ?? [];
    this.viewed = (call?.viewed ?? []).map((v) => new ByAt(v));
    this.isRead = call?.isRead ?? false;
    this.closed = call?.closed ? new ClosedByAt(call.closed) : undefined;
    this.timestamp = new Timestamp(call?.timestamp);
  }

  /**
   * Updates the entity in place. `assignees` and `viewed` are spliced rather
   * than reassigned so the array reference is preserved for Vue 2 reactivity
   * (e.g. a `v-model="call.assignees"` binding that mutates the array).
   */
  map(call?: MrfiktivRealtimeSessionViewModelGen) {
    this.id = call?.id ?? "";
    this.partnerId = call?.partnerId ?? "";
    this.from = call?.from ?? "";
    this.to = call?.to ?? "";
    this.status = (call?.status as CallStatusEnum) ?? CallStatusEnum.ACTIVE;
    this.startedAt = call?.startedAt ?? "";
    this.endedAt = call?.endedAt;
    this.duration = call?.duration;
    this.summary = call?.summary;
    this.personId = call?.personId;
    this.callSid = call?.callSid;
    this.realtimeSessionId = call?.realtimeSessionId;
    this.agentConfigId = call?.agentConfigId;
    this.transcriptItems = call?.transcriptItems;
    this.toolCalls = call?.toolCalls;
    this.callTracking = call?.callTracking;
    this.refs = call?.refs;
    this.metadata = call?.metadata;
    this.assignees.splice(0, this.assignees.length, ...(call?.assignees ?? []));
    this.viewed.splice(0, this.viewed.length, ...(call?.viewed ?? []).map((v) => new ByAt(v)));
    this.isRead = call?.isRead ?? false;
    this.closed = call?.closed ? new ClosedByAt(call.closed) : undefined;
    this.timestamp = new Timestamp(call?.timestamp);
  }

  async fetch(): Promise<this> {
    this.loading = true;
    try {
      const res = await callService.getOne(this.partnerId, this.id);

      this.map(res);

      CallDataAccessLayer.set(this);

      return this;
    } finally {
      this.loading = false;
    }
  }

  async setAssignees(assignees: string[]): Promise<this> {
    const res = await callService.setAssignees(this.partnerId, this.id, assignees);
    this.map(res);
    CallDataAccessLayer.set(this);
    return this;
  }

  async close(): Promise<this> {
    const res = await callService.close(this.partnerId, this.id);
    this.map(res);
    CallDataAccessLayer.set(this);
    return this;
  }

  async reopen(): Promise<this> {
    const res = await callService.reopen(this.partnerId, this.id);
    this.map(res);
    CallDataAccessLayer.set(this);
    return this;
  }

  /**
   * Mark this call as unread. Targets a backend endpoint that is not yet
   * implemented (see callService.markUnread) — will 404 until it ships.
   */
  async markUnread(): Promise<this> {
    const res = await callService.markUnread(this.partnerId, this.id);
    this.map(res);
    CallDataAccessLayer.set(this);
    return this;
  }

  static getUsage(query: MrfiktivRealtimeSessionControllerGetUsageParamsGen) {
    return callService.getUsage(query);
  }
}

type ICall = CallBase;
const Call = Filter.createForClass(CallBase);

export { ICall, Call };
